import React, { useState } from 'react';
import { ModalContent, ModalHeader, ModalBody, ModalFooter, Button } from '@heroui/react';
import { Icon } from '@iconify/react';
import { Typography, AlertBlock } from '../../../../components';
import { ResponsiveModal } from '../../../../components/ui';
import { useTranslations } from '../../../../contexts/translation.context';

interface ContentPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  modelName: string;
  mediaUrls: string[];
}

const ContentPreviewModal: React.FC<ContentPreviewModalProps> = ({
  isOpen,
  onClose,
  modelName,
  mediaUrls,
}) => {
  const { t } = useTranslations();
  const [fullscreenImage, setFullscreenImage] = useState<string | null>(null);

  const handleImageClick = (url: string) => {
    setFullscreenImage(url);
  };

  const handleCloseFullscreen = () => {
    setFullscreenImage(null);
  };

  const handleNextImage = () => {
    if (!fullscreenImage) return;
    const currentIndex = mediaUrls.indexOf(fullscreenImage);
    const nextIndex = (currentIndex + 1) % mediaUrls.length;
    setFullscreenImage(mediaUrls[nextIndex]);
  };

  const handlePrevImage = () => {
    if (!fullscreenImage) return;
    const currentIndex = mediaUrls.indexOf(fullscreenImage);
    const prevIndex = currentIndex === 0 ? mediaUrls.length - 1 : currentIndex - 1;
    setFullscreenImage(mediaUrls[prevIndex]);
  };

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={onClose}
      size="3xl"
      scrollBehavior="inside"
      classNames={{
        base: 'bg-white',
        header: 'border-b border-auth-surface',
        body: 'py-6',
        footer: 'border-t border-auth-surface',
      }}
    >
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex items-center justify-between px-4 md:px-6 py-4">
              <div className="flex items-center gap-2">
                <Typography variant="h6" className="text-auth-ink">
                  {t('partner.revshare.offers.contentPreview.title')} {modelName}
                </Typography>
              </div>
            </ModalHeader>

            <ModalBody className="px-4 md:px-6">
              {/* Info Banner */}
              <div className="mb-6">
                <AlertBlock
                  data={[
                    {
                      severity: 'info',
                      title: t('partner.revshare.offers.contentPreview.authorApproved'),
                      description: t('partner.revshare.offers.contentPreview.availableAfterLaunch'),
                    },
                  ]}
                />
              </div>

              {/* Media Grid */}
              <div className="grid grid-cols-4 gap-4">
                {mediaUrls.map((url, index) => (
                  <div
                    key={index}
                    className="aspect-[3/4] rounded-3 overflow-hidden bg-auth-surface cursor-pointer relative group"
                    onClick={() => handleImageClick(url)}
                  >
                    <img
                      src={url}
                      alt={`Preview ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                    {/* Hover Overlay */}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                      <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center">
                        <Icon icon="solar:eye-bold" width={24} className="text-partner" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Fullscreen Image Viewer */}
              {fullscreenImage && (
                <div
                  className="fixed inset-0 z-[9999] bg-black/80 flex items-center justify-center"
                  onClick={handleCloseFullscreen}
                >
                  {/* Close Button */}
                  <button
                    className="absolute top-0 right-0 z-10 size-12 flex items-center justify-center"
                    onClick={handleCloseFullscreen}
                  >
                    <Icon icon="lucide:x" width={24} className="text-white" />
                  </button>

                  {/* Previous Button */}
                  <button
                    className="absolute left-4 z-10 w-8 h-8 flex items-center justify-center rounded-2 bg-partner shadow-[0px_10px_15px_-3px_rgba(0,111,238,0.4),0px_4px_6px_-2px_rgba(0,0,0,0.05)] hover:bg-[#005FCC] transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePrevImage();
                    }}
                  >
                    <Icon icon="solar:alt-arrow-left-linear" width={16} className="text-white" />
                  </button>

                  {/* Next Button */}
                  <button
                    className="absolute right-4 z-10 w-8 h-8 flex items-center justify-center rounded-2 bg-partner shadow-[0px_10px_15px_-3px_rgba(0,111,238,0.4),0px_4px_6px_-2px_rgba(0,0,0,0.05)] hover:bg-[#005FCC] transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleNextImage();
                    }}
                  >
                    <Icon icon="solar:alt-arrow-right-linear" width={16} className="text-white" />
                  </button>

                  {/* Image */}
                  <img
                    src={fullscreenImage}
                    alt="Fullscreen preview"
                    className="max-w-[90vw] max-h-[90vh] object-contain"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              )}
            </ModalBody>

            <ModalFooter className="px-4 md:px-6 py-4">
              <Button color="primary" onPress={onClose} className="bg-partner text-white">
                {t('partner.revshare.offers.contentPreview.close')}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </ResponsiveModal>
  );
};

export default ContentPreviewModal;
